import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';

/*
 * /how-it-works — repurposed 3 Sep 2026 into "how a programme is made".
 *
 * The old page was a Capture / Insights / Outcomes product tour, written when
 * those were three separate pages. All three now redirect here, so the page had
 * become a shell for a structure that no longer exists, and its content
 * duplicated the pillars already covered on /individuals and /cmpx.
 *
 * It also still published "our digital biomarker Load Rate (LRE)". That is the
 * investor register and the exact construction QA-05 stripped from the
 * structured data and the other public pages. It has been out of the rest of
 * the site for weeks and survived here only because nothing linked to this page.
 *
 * WHAT THIS PAGE IS, AND WHAT IT IS NOT. This answers "how is my programme
 * made" — the process, in order, in plain language. It does NOT answer "who is
 * accountable for it", which is a clinical governance question and belongs to
 * /blog/who-writes-your-programme, where it is under review with a named
 * clinician attached. The two link to each other and must not converge: one is
 * a process explanation, the other is an accountability statement, and merging
 * them would put a YMYL claim on a marketing page.
 *
 * The URL is unchanged. /capture, /insights and /outcomes redirect here and
 * /how-it-works is in the live production sitemap, so renaming it would break
 * three redirects and drop an indexed URL to buy a tidier path.
 */

const HowItWorksPage = () => {
    useEffect(() => {
        document.title = 'How a programme is made | PhysioActivity';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', 'How a physiotherapy programme is built from your movement data, who writes it, and how it changes as you do.');
    }, []);

    return (
        <div className="pa2">

            <section className="hero" id="top">
                <div className="wrap two">
                    <div className="heromain">
                        <h1 className="on-dark">
                            <span className="setup">Nothing here generates</span>
                            <span className="payoff">your programme.</span>
                        </h1>
                        <p className="lede">
                            A registered physiotherapist writes it, working from how you actually
                            move rather than from what you can remember in an appointment. This is
                            what happens between those two things.
                        </p>
                        <p className="nerve">
                            The software proposes. A named clinician decides, and that decision is
                            recorded against them.
                        </p>
                        <div className="cta-row">
                            <Link
                                className="btn btn-t"
                                to="/individuals"
                                onClick={() => firePAEvent('how_cta_click', { source: 'hero' })}
                            >
                                Get started
                            </Link>
                        </div>
                    </div>

                    <div className="herovis">
                        <div className="dash" aria-label="How a programme is built, illustrative">
                            <div className="d-top"><i /><i /><i /><span className="w">CMPX &middot; programme build</span></div>
                            <div className="d-body">
                                <div className="d-side"><i className="on" /><i /><i /><i /><i /><i /></div>
                                <div className="d-main">
                                    <div className="d-h"><span className="t">Before writing</span><span className="s">Eight weeks of movement</span></div>
                                    <div className="d-tiles">
                                        <div><div className="v">56</div><div className="c">days of data</div></div>
                                        <div><div className="v g">3</div><div className="c">patterns flagged</div></div>
                                        <div><div className="v g">1</div><div className="c">clinician deciding</div></div>
                                    </div>
                                    <div className="d-chart">
                                        <div className="cl">What the physiotherapist sees</div>
                                        <svg viewBox="0 0 320 74" width="100%" height="74" aria-hidden="true">
                                            <polyline points="0,54 45,58 90,46 135,50 180,36 225,40 270,28 320,24" fill="none" stroke="#4CB6C3" strokeWidth="3.4" strokeLinecap="round" />
                                            <polyline points="0,66 45,64 90,65 135,62 180,61 225,60 270,59 320,58" fill="none" stroke="#cbd6e0" strokeWidth="2.2" strokeDasharray="5 5" />
                                        </svg>
                                    </div>
                                    <div className="d-foot"><span>Reviewed before each change</span><span>Written by a person</span></div>
                                </div>
                            </div>
                        </div>
                        <p className="cap">Illustrative example. Not a real programme.</p>
                    </div>
                </div>
            </section>

            <section className="tint" id="from">
                <div className="wrap">
                    <h2>
                        <span className="setup">What it gets built from.</span>
                    </h2>
                    <p className="lede">
                        Three inputs, and only one of them asks anything of you. The other two
                        arrive on their own, which is the point: what you did in week three is
                        still there in week nine.
                    </p>

                    <div className="cards">
                        <div className="card">
                            <h3>The phone in your pocket</h3>
                            <p>Movement and activity, captured in the background. No logging, no diary, and enough on its own to start.</p>
                        </div>
                        <div className="card">
                            <h3>A watch, if you have one</h3>
                            <p>Apple Watch, Fitbit, Garmin or Samsung add heart rate and sleep. It enriches the picture rather than being the thing that makes it work.</p>
                        </div>
                        <div className="card">
                            <h3>What you tell them</h3>
                            <p>Your goals, your history, what hurts and when. The data shows what happened; only you can say what you are trying to get back to.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="white" id="written">
                <div className="wrap">
                    <h2>
                        <span className="setup">How it gets written,</span>
                        <span className="payoff">and rewritten.</span>
                    </h2>
                    <p className="lede">
                        A programme is not a document you are handed once. It is a working
                        assumption that gets corrected as more of your movement arrives.
                    </p>

                    <div className="tiers">
                        <div className="tier">
                            <div className="k">First</div>
                            <h3>They look before they write</h3>
                            <ul>
                                <li>Your movement over weeks, not one snapshot</li>
                                <li>Patterns you would not notice yourself</li>
                                <li>Read against your goals and your history</li>
                            </ul>
                        </div>

                        <div className="tier">
                            <div className="k">Then</div>
                            <h3>A person writes it</h3>
                            <ul>
                                <li>A registered physiotherapist, named in the app</li>
                                <li>Their registration number is shown alongside</li>
                                <li>The software proposes; they decide what you get</li>
                            </ul>
                        </div>

                        <div className="tier">
                            <div className="k">After</div>
                            <h3>It changes as you do</h3>
                            <ul>
                                <li>Adjusted on what you actually did, not what you report</li>
                                <li>Updates reach you in SteadyStride the same day</li>
                                <li>If you stall, that is visible while it matters</li>
                            </ul>
                        </div>
                    </div>

                    <p className="quiet">
                        <Link to="/blog/who-writes-your-programme">Who writes your programme</Link>{' '}
                        is the separate question of who carries the clinical responsibility, and
                        where the software stops.{' '}
                        <Link to="/cmpx#roles">What CMPX does</Link> covers the practitioner side
                        of the same process.
                    </p>
                </div>
            </section>

            <section className="s-promise" id="start">
                <div className="wrap">
                    <h2 className="on-dark">
                        <span className="setup">Your watch counts the steps.</span>
                        <span className="payoff">A physio can tell you what to do.</span>
                    </h2>
                    <p className="lede">
                        Start with the phone you already have. If your employer covers it, it
                        costs you nothing.
                    </p>
                    <div className="cta-row">
                        <Link
                            className="btn btn-t"
                            to="/individuals"
                            onClick={() => firePAEvent('how_cta_click', { source: 'closing' })}
                        >
                            Get started
                        </Link>
                        <Link className="btn btn-o" to="/physiotherapists">
                            I am a physiotherapist
                        </Link>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default HowItWorksPage;
